import type { DropHistoryItem } from '@/api/hooks/useOrderHistory'
import {
  formatTimestamp,
  getMappingDisplay,
} from '@/pages/Dashboard/panels/ConnectionLogsPanel/components/LogDetailsPanel/OrderChangeSummary/utils'
import { ArrowRightOutlined } from '@ant-design/icons'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Card, Table, type TableColumnsType, theme } from 'antd'
import { AlertCircle, ChevronDown, ChevronUp, Droplet } from 'lucide-react'
import { useState } from 'react'

interface DropCardProps {
  drop: DropHistoryItem
}

export function DropCard({ drop }: DropCardProps) {
  const [expanded, setExpanded] = useState(false)
  const { token } = theme.useToken()
  const isError = drop.status === 'error'
  const isRejected = drop.status === 'rejected'
  const statusColor = isError ? token.colorError : token.colorWarning

  // Drop detail columns
  const detailColumns: TableColumnsType<
    (typeof drop.origin_drop.details)[number]
  > = [
    {
      title: 'Product',
      dataIndex: 'product',
      render: (p) => getMappingDisplay(p) ?? '—',
    },
    {
      title: 'Volume',
      dataIndex: 'quantity',
      render: (v: number) => (v ? `${v.toLocaleString()} gal` : '—'),
    },
    {
      title: 'Tank',
      dataIndex: 'tank',
      render: (t) => getMappingDisplay(t) ?? '—',
    },
    {
      title: 'Pre-Drop',
      dataIndex: 'pre_drop_volume',
      render: (v: number | null) => (v ? `${v.toLocaleString()} gal` : '—'),
    },
    {
      title: 'Post-Drop',
      dataIndex: 'post_drop_volume',
      render: (v: number | null) => (v ? `${v.toLocaleString()} gal` : '—'),
    },
  ]

  return (
    <Horizontal gap={12} style={{ position: 'relative' }}>
      {/* Timeline dot */}
      <div
        style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor:
            isError || isRejected ? statusColor : token.colorTextQuaternary,
          flexShrink: 0,
          marginTop: 18,
          zIndex: 1,
        }}
      />

      <Card
        size='small'
        style={{
          flex: 1,
          cursor: 'pointer',
          transition: 'background-color 0.2s',
          borderLeft:
            isError || isRejected ? `3px solid ${statusColor}` : undefined,
          backgroundColor:
            isError || isRejected
              ? `color-mix(in srgb, ${statusColor} 5%, transparent)`
              : undefined,
        }}
        styles={{ body: { padding: 12 } }}
        onClick={() => setExpanded(!expanded)}
      >
        <Vertical gap={8}>
          {/* Row 1: Drop + Site + Timestamp */}
          <Horizontal justifyContent='space-between' alignItems='center'>
            <Horizontal gap={8} alignItems='center'>
              <Droplet
                size={16}
                color={token.colorSuccess}
                style={{ flexShrink: 0 }}
              />
              <Texto weight='600'>
                Drop at {drop.site_name ?? 'Unknown Site'}
              </Texto>
            </Horizontal>
            <Horizontal gap={8} alignItems='center'>
              <Texto appearance='hint' style={{ fontSize: 12 }}>
                {formatTimestamp(drop.created_on)}
              </Texto>
              {expanded ? (
                <ChevronUp size={16} color={token.colorTextTertiary} />
              ) : (
                <ChevronDown size={16} color={token.colorTextTertiary} />
              )}
            </Horizontal>
          </Horizontal>

          {/* Row 2: Direction */}
          <Horizontal gap={6} alignItems='center'>
            <Texto weight='600' style={{ fontSize: 13 }}>
              {drop.origin_tenant_name}
            </Texto>
            <ArrowRightOutlined
              style={{ fontSize: 10, color: token.colorTextTertiary }}
            />
            <Texto weight='600' style={{ fontSize: 13 }}>
              {drop.destination_tenant_name ?? 'N/A'}
            </Texto>
          </Horizontal>

          {/* Row 3: Summary - Volume, Detail Count + Error indicator */}
          <Horizontal justifyContent='space-between' alignItems='center'>
            <Horizontal gap={12} alignItems='center'>
              <Texto appearance='hint' style={{ fontSize: 12 }}>
                {drop.total_quantity.toLocaleString()} gal ({drop.detail_count}{' '}
                products)
              </Texto>
            </Horizontal>
            {(isError || isRejected) && (
              <Horizontal gap={4} alignItems='center'>
                <AlertCircle size={12} color={statusColor} />
                <Texto style={{ fontSize: 12, color: statusColor }}>
                  {isRejected ? 'Rejected' : 'Error'}
                </Texto>
              </Horizontal>
            )}
          </Horizontal>

          {/* Expanded: Detail Table */}
          {expanded && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                borderTop: `1px solid ${token.colorBorderSecondary}`,
                marginTop: 12,
                paddingTop: 12,
                cursor: 'default',
              }}
            >
              <Texto
                style={{
                  fontSize: 12,
                  marginBottom: 16,
                  color: token.colorTextSecondary,
                }}
              >
                {drop.mode === 'replace'
                  ? 'The following drops replaced the existing drops on the order'
                  : 'The following drops were appended to the order'}
              </Texto>
              <Table
                dataSource={drop.origin_drop.details}
                columns={detailColumns}
                size='small'
                pagination={false}
                rowKey={(_, idx) => String(idx)}
              />
            </div>
          )}
        </Vertical>
      </Card>
    </Horizontal>
  )
}
